/**
 * PWA Install Prompt — platform-aware install banner.
 *
 * - Android/Chrome: Intercepts `beforeinstallprompt`, shows install button.
 * - iOS Safari: Detects platform, shows "Add to Home Screen" instructions.
 * - Already installed (standalone): Shows nothing.
 *
 * Dismissed state persists in localStorage for 7 days.
 */

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'android-chrome' | 'ios-safari' | 'unsupported';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

import { t } from '../../i18n';

const STORAGE_KEY = 'aftercare-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 3000;
const BANNER_ID = 'install-prompt-banner';

// -----------------------------------------------------------------------
// Platform detection
// -----------------------------------------------------------------------

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua)) {
    return 'ios-safari';
  }

  // Chrome on Android, Edge, Samsung Internet, or desktop Chrome
  if ('BeforeInstallPromptEvent' in window || /Android/.test(ua)) {
    return 'android-chrome';
  }

  return 'unsupported';
}

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;

  const dismissedAt = parseInt(raw, 10);
  if (isNaN(dismissedAt)) return false;

  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

function markDismissed(): void {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

// -----------------------------------------------------------------------
// Styles (injected once)
// -----------------------------------------------------------------------

function injectStyles(): void {
  if (document.getElementById('install-prompt-styles')) return;

  const style = document.createElement('style');
  style.id = 'install-prompt-styles';
  style.textContent = `
    #${BANNER_ID} {
      position: fixed;
      bottom: calc(var(--nav-height) + var(--safe-bottom, 0px));
      left: 0;
      right: 0;
      z-index: 1500;
      transform: translateY(100%);
      transition: transform var(--transition-slow);
    }

    #${BANNER_ID}.visible {
      transform: translateY(0);
    }

    #${BANNER_ID}.hiding {
      transform: translateY(100%);
    }

    .install-prompt-inner {
      display: flex;
      align-items: center;
      gap: var(--space-md);
      margin: 0 var(--space-md) var(--space-sm) var(--space-md);
      padding: var(--space-md) var(--space-lg);
      background: linear-gradient(135deg, #ffffff 0%, #f0ebe4 100%);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
      position: relative;
    }

    .install-prompt-icon {
      font-size: 36px;
      line-height: 1;
      flex-shrink: 0;
    }

    .install-prompt-text {
      flex: 1;
      min-width: 0;
    }

    .install-prompt-title {
      font-size: var(--font-size-large);
      font-weight: 700;
      color: var(--color-text);
      line-height: var(--line-height-tight);
    }

    .install-prompt-subtitle {
      font-size: var(--font-size-small);
      color: var(--color-text-muted);
      line-height: var(--line-height-tight);
      margin-top: 2px;
    }

    .install-prompt-action {
      flex-shrink: 0;
    }

    .install-prompt-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 48px;
      padding: 12px 20px;
      border: none;
      border-radius: var(--radius-md);
      background: linear-gradient(135deg, #0d9488 0%, #2563eb 100%);
      color: #ffffff;
      font-family: var(--font-family);
      font-size: var(--font-size-body);
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      transition: opacity var(--transition-fast), transform var(--transition-fast);
      white-space: nowrap;
    }

    .install-prompt-btn:hover,
    .install-prompt-btn:focus-visible {
      opacity: 0.9;
    }

    .install-prompt-btn:active {
      transform: scale(0.97);
    }

    .install-prompt-dismiss {
      position: absolute;
      top: var(--space-xs);
      right: var(--space-xs);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-text-dim);
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      touch-action: manipulation;
      transition: color var(--transition-fast), background-color var(--transition-fast);
      padding: 0;
    }

    .install-prompt-dismiss:hover,
    .install-prompt-dismiss:focus-visible {
      color: var(--color-text);
      background: rgba(255, 255, 255, 0.1);
    }

    .install-prompt-ios-steps {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: var(--space-sm);
    }

    .install-prompt-ios-step {
      font-size: var(--font-size-small);
      color: var(--color-text-muted);
      line-height: var(--line-height-tight);
    }

    .install-prompt-ios-step strong {
      color: var(--color-text);
    }

    .install-prompt-share-icon {
      display: inline-block;
      vertical-align: middle;
      font-size: 18px;
    }

    @media (prefers-reduced-motion: reduce) {
      #${BANNER_ID},
      #${BANNER_ID}.visible,
      #${BANNER_ID}.hiding {
        transition-duration: 0.01ms !important;
      }
    }
  `;

  document.head.appendChild(style);
}

// -----------------------------------------------------------------------
// Safe DOM helpers
// -----------------------------------------------------------------------

/**
 * Build an iOS instruction step using safe DOM methods (no innerHTML).
 */
function createIOSStep1(): HTMLElement {
  const step = document.createElement('div');
  step.className = 'install-prompt-ios-step';

  step.appendChild(document.createTextNode(t('install.iosStep1Prefix')));

  const bold = document.createElement('strong');
  bold.textContent = t('install.iosShare');
  step.appendChild(bold);

  step.appendChild(document.createTextNode(' '));

  // iOS share icon — SVG replica of the actual square-with-arrow-up icon
  const shareIcon = document.createElement('span');
  shareIcon.className = 'install-prompt-share-icon';
  shareIcon.setAttribute('aria-label', 'share icon');
  shareIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;color:#007AFF"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>';
  step.appendChild(shareIcon);

  step.appendChild(document.createTextNode(t('install.iosInSafari')));

  return step;
}

function createIOSStep2(): HTMLElement {
  const step = document.createElement('div');
  step.className = 'install-prompt-ios-step';

  step.appendChild(document.createTextNode(t('install.iosStep2Prefix')));

  const bold = document.createElement('strong');
  bold.textContent = t('install.iosAddToHome');
  step.appendChild(bold);

  return step;
}

// -----------------------------------------------------------------------
// Banner creation
// -----------------------------------------------------------------------

let deferredPromptEvent: BeforeInstallPromptEvent | null = null;

function createBanner(platform: Platform): HTMLElement {
  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'complementary');
  banner.setAttribute('aria-label', t('install.ariaLabel'));

  const inner = document.createElement('div');
  inner.className = 'install-prompt-inner';

  // Icon
  const icon = document.createElement('span');
  icon.className = 'install-prompt-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '\u2764\uFE0F\u200D\uD83E\uDE79'; // heart + bandage
  inner.appendChild(icon);

  // Text block
  const textBlock = document.createElement('div');
  textBlock.className = 'install-prompt-text';

  const title = document.createElement('div');
  title.className = 'install-prompt-title';
  title.textContent = t('install.title');
  textBlock.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.className = 'install-prompt-subtitle';
  subtitle.textContent = t('install.subtitle');
  textBlock.appendChild(subtitle);

  if (platform === 'ios-safari') {
    const steps = document.createElement('div');
    steps.className = 'install-prompt-ios-steps';
    steps.appendChild(createIOSStep1());
    steps.appendChild(createIOSStep2());
    textBlock.appendChild(steps);
  }

  inner.appendChild(textBlock);

  // Action button (Android/Chrome only)
  if (platform === 'android-chrome') {
    const actionWrap = document.createElement('div');
    actionWrap.className = 'install-prompt-action';

    const installBtn = document.createElement('button');
    installBtn.className = 'install-prompt-btn';
    installBtn.textContent = t('install.button');
    installBtn.setAttribute('aria-label', t('install.ariaLabel'));

    installBtn.addEventListener('click', async () => {
      if (!deferredPromptEvent) return;

      try {
        await deferredPromptEvent.prompt();
        const choice = await deferredPromptEvent.userChoice;

        if (choice.outcome === 'accepted') {
          console.log('PWA install accepted');
        } else {
          console.log('PWA install dismissed');
        }
      } catch (err) {
        console.warn('Install prompt error:', err);
      }

      deferredPromptEvent = null;
      removeBanner();
    });

    actionWrap.appendChild(installBtn);
    inner.appendChild(actionWrap);
  }

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'install-prompt-dismiss';
  dismissBtn.setAttribute('aria-label', t('install.dismiss'));
  dismissBtn.textContent = '\u00D7'; // multiplication sign (x)

  dismissBtn.addEventListener('click', () => {
    markDismissed();
    removeBanner();
  });

  inner.appendChild(dismissBtn);
  banner.appendChild(inner);

  return banner;
}

// -----------------------------------------------------------------------
// Show / remove
// -----------------------------------------------------------------------

function removeBanner(): void {
  const banner = document.getElementById(BANNER_ID);
  if (!banner) return;

  banner.classList.remove('visible');
  banner.classList.add('hiding');

  const onEnd = () => {
    banner.removeEventListener('transitionend', onEnd);
    banner.remove();
  };

  banner.addEventListener('transitionend', onEnd);

  // Fallback removal in case transitionend never fires (reduced motion)
  setTimeout(() => {
    if (document.getElementById(BANNER_ID)) {
      banner.remove();
    }
  }, 600);
}

function showBanner(platform: Platform): void {
  // Don't double-create
  if (document.getElementById(BANNER_ID)) return;

  injectStyles();

  const banner = createBanner(platform);
  document.body.appendChild(banner);

  // Trigger slide-up on next frame
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      banner.classList.add('visible');
    });
  });
}

// -----------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------

/**
 * Initializes the PWA install prompt.
 * Call once from main.ts after the app has loaded.
 *
 * - On Android/Chrome: listens for `beforeinstallprompt` then shows banner.
 * - On iOS Safari: shows instructional banner after delay.
 * - If already installed or recently dismissed: does nothing.
 */
export function showInstallPrompt(): void {
  // Already installed — nothing to do
  if (isStandalone()) return;

  // Recently dismissed — respect the user's choice
  if (wasDismissedRecently()) return;

  const platform = detectPlatform();

  if (platform === 'unsupported') return;

  if (platform === 'android-chrome') {
    // Listen for the browser's install eligibility event
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      deferredPromptEvent = e as BeforeInstallPromptEvent;

      // Show banner after delay
      setTimeout(() => {
        if (deferredPromptEvent && !wasDismissedRecently() && !isStandalone()) {
          showBanner('android-chrome');
        }
      }, SHOW_DELAY_MS);
    });

    // Auto-hide if app gets installed while banner is showing
    window.addEventListener('appinstalled', () => {
      console.log('AfterCare installed successfully');
      removeBanner();
      deferredPromptEvent = null;
    });

    return;
  }

  if (platform === 'ios-safari') {
    // iOS has no beforeinstallprompt — show instructions after delay
    setTimeout(() => {
      if (!wasDismissedRecently() && !isStandalone()) {
        showBanner('ios-safari');
      }
    }, SHOW_DELAY_MS);
  }
}
